// spokeGrowthMp4Export
//
// Client-side MP4 export for the Spoke Growth animation.
//
// Strategy: draw every animation frame onto an offscreen canvas ourselves
// (not the Google Map component) so we get deterministic output at any
// FPS regardless of what the user is doing in the tab. The base map
// picture is a single Google Static Maps request sized to enclose all
// hubs + spokes; we then overlay hubs (hospital-cross icon) and spokes
// (circle for inbound, diamond for outbound) at their projected screen
// coords, colored by cumulative referral count using the same colormap
// the interactive view uses.
//
// Video encode path:
//   1) Preferred — WebCodecs VideoEncoder (H.264) + mp4-muxer. Native in
//      Chromium 94+, Safari 17+, Firefox 130+. No SharedArrayBuffer /
//      ffmpeg.wasm required. This produces a real .mp4.
//   2) Fallback — MediaRecorder on canvas.captureStream. Produces a
//      .webm; we still write it to disk with the "mp4" filename stem
//      but the extension is honest. Included so users on browsers
//      without WebCodecs at least get *something*.
//
// The renderer is intentionally self-contained (no Google Maps JS API
// calls) so it works in a headless render loop and doesn't fight the
// interactive map's WebGL state. Any missing pieces (custom base map
// tiles, animated arcs, etc.) can be layered onto this canvas later.

import { sampleColormap, spokeMarkerSize, lerpHex } from './spokeGrowthColormap';
import { DIRECTION_OUTBOUND } from './referralDirection';

const EXPORT_WIDTH = 1280;
const EXPORT_HEIGHT = 720;
const EXPORT_FPS = 30;

// Google Static Maps free size limit is 640x640 (or 1280x1280 at 2x
// scale for premium). We stay at 1280x720 with scale=2 which streams
// as a single Maps Static tile and matches our video canvas 1:1.
const STATIC_MAPS_URL = 'https://maps.googleapis.com/maps/api/staticmap';
const STATIC_MAPS_SCALE = 2;

// Static Maps encodes styles as repeated `style=` params in the form
// `feature:X|element:Y|rule:value|rule:value`. This mirrors the same
// preset ids used by the interactive map so the video matches on-screen
// output. Keep the presets aligned with MAP_STYLES in SpokeGrowth.js.
//
// The `blank` preset returns `null` — the renderer treats that as "skip
// the base map entirely" and draws on a solid background color instead.
function styleParamsForPreset(mapStyleId) {
  switch (mapStyleId) {
    case 'terrain':
      return { maptype: 'terrain', styles: [] };
    case 'satellite':
      return { maptype: 'hybrid', styles: [] };
    case 'muted':
      return {
        maptype: 'roadmap',
        styles: [
          'element:geometry|saturation:-80|lightness:20',
          'element:labels.text.fill|color:0x9ca3af',
          'element:labels.text.stroke|color:0xffffff',
          'feature:poi|visibility:off',
          'feature:transit|visibility:off',
          'feature:road.local|element:labels|visibility:off',
        ],
      };
    case 'faint':
      return {
        maptype: 'roadmap',
        styles: [
          'element:geometry|color:0xf8fafc',
          'element:labels|visibility:off',
          'feature:water|element:geometry|color:0xe2e8f0',
          'feature:landscape|element:geometry|color:0xf1f5f9',
          'feature:road|visibility:off',
          'feature:poi|visibility:off',
          'feature:transit|visibility:off',
          'feature:administrative|element:geometry.stroke|color:0xcbd5e1|weight:0.6',
        ],
      };
    case 'dark':
      return {
        maptype: 'roadmap',
        styles: [
          'element:geometry|color:0x1f2937',
          'element:labels.text.fill|color:0x9ca3af',
          'element:labels.text.stroke|color:0x111827',
          'feature:road|element:geometry|color:0x374151',
          'feature:water|element:geometry|color:0x0f172a',
          'feature:poi|visibility:off',
          'feature:transit|visibility:off',
          'feature:administrative|element:geometry.stroke|color:0x374151',
        ],
      };
    case 'blank':
      return null;
    case 'standard':
    default:
      // Standard road map, minus POI/transit noise so the dots read cleanly.
      return {
        maptype: 'roadmap',
        styles: [
          'feature:poi|visibility:off',
          'feature:transit|visibility:off',
        ],
      };
  }
}

// Background fill for frames when the base map is skipped (`blank`) or
// when the Static Maps request fails. Aligns with the interactive map's
// backgroundColor per preset so `Blank` and `Dark` still feel intentional.
function backgroundColorForPreset(mapStyleId) {
  if (mapStyleId === 'dark') return '#111827';
  return '#f8fafc';
}

// Web Mercator projection to screen space. All export math happens in
// pixels relative to the export canvas, driven by a computed viewport
// (center + zoom) that encloses every point with a small padding.
const TILE_SIZE = 256;

function project(lat, lng) {
  const siny = Math.sin((lat * Math.PI) / 180);
  const clampedSiny = Math.max(Math.min(siny, 0.9999), -0.9999);
  return {
    x: TILE_SIZE * (0.5 + lng / 360),
    y: TILE_SIZE * (0.5 - Math.log((1 + clampedSiny) / (1 - clampedSiny)) / (4 * Math.PI)),
  };
}

function computeViewport(points, width, height, paddingPx = 80) {
  // Find lat/lng bounds.
  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;
  for (const p of points) {
    if (p.lat == null || p.lng == null) continue;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  if (!Number.isFinite(minLat)) {
    return { center: { lat: 39.83, lng: -98.58 }, zoom: 4 };
  }
  // Pad the bounds slightly so markers near the edges don't clip.
  const latPad = Math.max(0.05, (maxLat - minLat) * 0.05);
  const lngPad = Math.max(0.05, (maxLng - minLng) * 0.05);
  minLat -= latPad; maxLat += latPad;
  minLng -= lngPad; maxLng += lngPad;

  const bl = project(minLat, minLng);
  const tr = project(maxLat, maxLng);
  const worldWidth = tr.x - bl.x;
  const worldHeight = bl.y - tr.y; // y grows downward
  if (worldWidth <= 0 || worldHeight <= 0) {
    return { center: { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 }, zoom: 10 };
  }
  const usableWidth = width - paddingPx * 2;
  const usableHeight = height - paddingPx * 2;
  const zoomX = Math.log2(usableWidth / worldWidth);
  const zoomY = Math.log2(usableHeight / worldHeight);
  const zoom = Math.floor(Math.min(zoomX, zoomY));
  return {
    center: { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 },
    zoom: Math.max(2, Math.min(zoom, 15)),
  };
}

function latLngToPixel(lat, lng, center, zoom, width, height) {
  const scale = Math.pow(2, zoom);
  const world = project(lat, lng);
  const centerWorld = project(center.lat, center.lng);
  return {
    x: (world.x - centerWorld.x) * scale + width / 2,
    y: (world.y - centerWorld.y) * scale + height / 2,
  };
}

async function loadStaticMap(center, zoom, width, height, mapStyleId) {
  const preset = styleParamsForPreset(mapStyleId);
  if (preset === null) return null;  // 'blank' — caller renders on flat bg.
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) return null;
  const stylesQs = (preset.styles || []).map(s => `&style=${encodeURIComponent(s)}`).join('');
  const url =
    `${STATIC_MAPS_URL}?center=${center.lat},${center.lng}` +
    `&zoom=${zoom}&size=${Math.floor(width / STATIC_MAPS_SCALE)}x${Math.floor(height / STATIC_MAPS_SCALE)}` +
    `&scale=${STATIC_MAPS_SCALE}&maptype=${preset.maptype}` +
    stylesQs +
    `&key=${encodeURIComponent(key)}`;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err instanceof Error ? err : new Error('Static maps request failed'));
    img.src = url;
  });
}

function drawHubIcon(ctx, x, y, size = 22) {
  // Filled cross (hospital) so hubs read as hospitals even at video
  // resolution. Drawn as two overlapping rounded rectangles.
  const arm = size * 0.32;
  const half = size / 2;
  ctx.fillStyle = '#0f766e';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.beginPath();
  // Vertical bar
  ctx.rect(x - arm / 2, y - half, arm, size);
  // Horizontal bar
  ctx.rect(x - half, y - arm / 2, size, arm);
  ctx.fill();
  ctx.stroke();
}

const POP_COLOR = '#ff00aa';
const POP_STROKE = '#ffffff';
const POP_DURATION_MS = 450;

function popIntensity(playhead, lastT, startMs, endMs, durationMs) {
  if (lastT == null || playhead >= 0.999) return 0;
  const rangeMs = Math.max(1, endMs - startMs);
  const popWindow = Math.min(0.08, Math.max(0.008, POP_DURATION_MS / Math.max(1, durationMs)));
  const hitPlayhead = (lastT - startMs) / rangeMs;
  const raw = (playhead - hitPlayhead) / popWindow;
  if (raw < 0 || raw >= 1) return 0;
  const hold = 0.15;
  if (raw <= hold) return 1;
  const fade = 1 - (raw - hold) / (1 - hold);
  return fade * fade;
}

function drawSpokeIcon(ctx, x, y, count, direction, source, colormapId, colormapMax, pop = 0) {
  const baseSize = spokeMarkerSize(count, colormapMax || 1, { minSize: 8, maxSize: 30 });
  const size = baseSize * (1 + 0.9 * pop);
  const baseColor = sampleColormap(colormapId, count, colormapMax || 1);
  // Never fully replace heatmap color; pulse always settles back to base.
  const color = pop > 0.02 ? lerpHex(baseColor, POP_COLOR, Math.min(0.85, pop * 0.85)) : baseColor;
  ctx.fillStyle = color;
  ctx.strokeStyle = pop > 0.05 ? POP_STROKE : '#111827';
  ctx.lineWidth = pop > 0.05 ? 2.75 : 1.5;
  if (source === 'non-ve') {
    // Triangle — placeholder for future non-VE physician spokes.
    const r = size / 2;
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r * 0.9, y + r * 0.8);
    ctx.lineTo(x - r * 0.9, y + r * 0.8);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    return;
  }
  if (direction === DIRECTION_OUTBOUND) {
    const r = size / 2;
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r, y);
    ctx.lineTo(x, y + r);
    ctx.lineTo(x - r, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(x, y, size / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

function drawFrameHud(ctx, canvasW, canvasH, currentDate) {
  // Bottom-left timestamp badge so exports remain legible when scrubbed.
  const label = currentDate ? new Date(currentDate).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  }) : '';
  if (!label) return;
  ctx.font = 'bold 20px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const paddingX = 12;
  const paddingY = 8;
  const width = ctx.measureText(label).width + paddingX * 2;
  const height = 34;
  ctx.fillStyle = 'rgba(15, 23, 42, 0.72)';
  ctx.fillRect(16, canvasH - 16 - height, width, height);
  ctx.fillStyle = '#f8fafc';
  ctx.fillText(label, 16 + paddingX, canvasH - 16 - paddingY - 6);
}

function currentStateAtTime(events, timeMs) {
  const byId = new Map();
  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    if (ev.t > timeMs) break;
    let sp = byId.get(ev.spokeVeId);
    if (!sp) {
      sp = {
        veId: ev.spokeVeId,
        direction: ev.direction,
        source: ev.source,
        count: 0,
        firstT: ev.t,
        lastT: ev.t,
        lat: ev.lat,
        lng: ev.lng,
      };
      byId.set(ev.spokeVeId, sp);
    }
    sp.count += 1;
    sp.lastT = ev.t;
  }
  return Array.from(byId.values());
}

const FLY_IN_DURATION_MS = 700;

function flyInProgress(playhead, firstT, startMs, endMs, durationMs) {
  const rangeMs = Math.max(1, endMs - startMs);
  const flyWindow = Math.min(0.12, Math.max(0.012, FLY_IN_DURATION_MS / Math.max(1, durationMs)));
  const spokeFirstPlayhead = (firstT - startMs) / rangeMs;
  const raw = (playhead - spokeFirstPlayhead) / flyWindow;
  const clamped = Math.max(0, Math.min(1, raw));
  return 1 - Math.pow(1 - clamped, 3);
}

/**
 * Render one frame onto `ctx` for the given playhead position.
 */
function renderFrame({
  ctx,
  width,
  height,
  baseImage,
  bgColor,
  center,
  zoom,
  hubs,
  events,
  playhead,
  startMs,
  endMs,
  colormapId,
  lineEffect = 'icons',
  lineColorMode = 'black',
  customLineColor = '#000000',
  popOnHit = true,
  durationMs = 30000,
}) {
  ctx.fillStyle = bgColor || '#f1f5f9';
  ctx.fillRect(0, 0, width, height);
  if (baseImage) {
    ctx.drawImage(baseImage, 0, 0, width, height);
  }

  const t = startMs + (endMs - startMs) * playhead;
  const visibleSpokes = currentStateAtTime(events, t);
  let colormapMax = 0;
  for (const sp of visibleSpokes) if (sp.count > colormapMax) colormapMax = sp.count;
  colormapMax = Math.max(1, colormapMax);

  const resolveLineColor = (count) => {
    if (lineColorMode === 'colormap') return sampleColormap(colormapId, count, colormapMax);
    if (lineColorMode === 'custom') return customLineColor || '#000000';
    return '#000000';
  };
  const baseLineOpacity = lineColorMode === 'colormap' ? 0.55 : 0.8;

  // Connection lines: none / full static / fly-in from spoke toward hub.
  if (lineEffect === 'static' || lineEffect === 'flyin') {
    ctx.save();
    for (const sp of visibleSpokes) {
      if (sp.lat == null || sp.lng == null) continue;
      const spPx = latLngToPixel(sp.lat, sp.lng, center, zoom, width, height);
      let nearestPx = null;
      let bestD = Infinity;
      for (const h of hubs) {
        const hp = latLngToPixel(h.lat, h.lng, center, zoom, width, height);
        const dx = hp.x - spPx.x, dy = hp.y - spPx.y;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; nearestPx = hp; }
      }
      if (!nearestPx) continue;

      let progress = 1;
      if (lineEffect === 'flyin') {
        progress = flyInProgress(playhead, sp.firstT ?? t, startMs, endMs, durationMs);
      }
      if (progress <= 0.001) continue;

      const tipX = spPx.x + (nearestPx.x - spPx.x) * progress;
      const tipY = spPx.y + (nearestPx.y - spPx.y) * progress;
      ctx.strokeStyle = resolveLineColor(sp.count);
      ctx.globalAlpha = lineEffect === 'flyin' && progress < 1
        ? Math.min(0.95, baseLineOpacity + 0.2)
        : baseLineOpacity;
      ctx.lineWidth = 1 + Math.min(3, Math.sqrt(sp.count));
      ctx.beginPath();
      ctx.moveTo(spPx.x, spPx.y);
      ctx.lineTo(tipX, tipY);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Spokes first so hubs sit visually on top.
  for (const sp of visibleSpokes) {
    if (sp.lat == null || sp.lng == null) continue;
    const px = latLngToPixel(sp.lat, sp.lng, center, zoom, width, height);
    const pop = popOnHit
      ? popIntensity(playhead, sp.lastT, startMs, endMs, durationMs)
      : 0;
    drawSpokeIcon(ctx, px.x, px.y, sp.count, sp.direction, sp.source, colormapId, colormapMax, pop);
  }
  for (const h of hubs) {
    const px = latLngToPixel(h.lat, h.lng, center, zoom, width, height);
    drawHubIcon(ctx, px.x, px.y, 26);
  }

  drawFrameHud(ctx, width, height, t);
}

async function encodeWithWebCodecs({
  canvas,
  totalFrames,
  fps,
  drawFrame,
  onProgress,
}) {
  const Muxer = (await import('mp4-muxer')).Muxer;
  const ArrayBufferTarget = (await import('mp4-muxer')).ArrayBufferTarget;
  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: {
      codec: 'avc',
      width: canvas.width,
      height: canvas.height,
      frameRate: fps,
    },
    fastStart: 'in-memory',
  });
  // Prefer a safe H.264 profile that most Chromium/Safari builds support.
  const codec = 'avc1.4d0028'; // Main profile, level 4.0
  const encoder = new window.VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (err) => { throw err; },
  });
  encoder.configure({
    codec,
    width: canvas.width,
    height: canvas.height,
    bitrate: 4_000_000,
    framerate: fps,
  });

  const usPerFrame = Math.round(1_000_000 / fps);
  for (let i = 0; i < totalFrames; i++) {
    drawFrame(i / Math.max(1, totalFrames - 1));
    const frame = new window.VideoFrame(canvas, {
      timestamp: i * usPerFrame,
      duration: usPerFrame,
    });
    // Force a keyframe on the first frame (and every ~2s of frames) so
    // seeking / thumbnails work in QuickTime and browser players.
    const keyFrame = i === 0 || i % (fps * 2) === 0;
    encoder.encode(frame, { keyFrame });
    frame.close();
    if (onProgress) onProgress(i / totalFrames);
    // Yield to the browser occasionally so the UI stays responsive.
    if (i % 6 === 0) await new Promise(r => setTimeout(r, 0));
  }
  await encoder.flush();
  encoder.close();
  muxer.finalize();
  const buf = muxer.target.buffer;
  return new Blob([buf], { type: 'video/mp4' });
}

async function encodeWithMediaRecorder({
  canvas,
  totalFrames,
  fps,
  drawFrame,
  onProgress,
}) {
  const stream = canvas.captureStream(0);
  const track = stream.getVideoTracks()[0];
  const mimeType = [
    'video/mp4;codecs=avc1.4d0028',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ].find(t => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || 'video/webm';

  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 4_000_000 });
  const chunks = [];
  const done = new Promise((resolve, reject) => {
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    recorder.onstop = () => resolve();
    recorder.onerror = reject;
  });

  recorder.start(100);
  const frameDelay = 1000 / fps;
  for (let i = 0; i < totalFrames; i++) {
    drawFrame(i / Math.max(1, totalFrames - 1));
    // In some browsers requestFrame is needed to advance a 0-fps stream.
    if (typeof track.requestFrame === 'function') track.requestFrame();
    await new Promise(r => setTimeout(r, frameDelay));
    if (onProgress) onProgress(i / totalFrames);
  }
  recorder.stop();
  await done;
  return new Blob(chunks, { type: mimeType.startsWith('video/mp4') ? 'video/mp4' : 'video/webm' });
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

/**
 * Render the Spoke Growth timeline to an .mp4 file (or .webm as a
 * fallback on browsers without WebCodecs) and trigger a download.
 *
 * @param {object} opts
 * @param {object} opts.timeline      Timeline object with events + start/endMs.
 * @param {any[]}  opts.hubs          Hub facilities with lat/lng.
 * @param {string} opts.colormapId    Colormap key from spokeGrowthColormap.
 * @param {number} opts.durationMs    Target animation duration in ms.
 * @param {string} opts.filenameStem  Filename without extension.
 * @param {{center:{lat:number,lng:number}, zoom:number}|null} [opts.viewport]
 *   Optional live map view. When provided, the export uses this center/
 *   zoom instead of auto-fitting every hub + spoke (which often zooms
 *   out to the whole country).
 * @param {(p:number,msg?:string)=>void} [opts.onProgress]
 */
export async function exportSpokeGrowthMp4({
  timeline,
  hubs,
  colormapId,
  durationMs,
  filenameStem,
  mapStyleId = 'standard',
  lineEffect = 'icons',
  lineColorMode = 'black',
  customLineColor = '#000000',
  popOnHit = true,
  // Back-compat if an older caller still passes showLines.
  showLines = false,
  viewport: viewportOverride = null,
  onProgress,
}) {
  const resolvedLineEffect = lineEffect || (showLines ? 'static' : 'icons');
  if (!timeline || !timeline.events?.length) {
    throw new Error('Timeline is empty; nothing to export.');
  }
  const width = EXPORT_WIDTH;
  const height = EXPORT_HEIGHT;
  const fps = EXPORT_FPS;
  const totalFrames = Math.max(2, Math.round((durationMs / 1000) * fps));

  onProgress?.(0.01, 'Setting up canvas…');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas not available.');

  // Prefer the interactive map's current view so the video matches what
  // the user zoomed/panned to. Fall back to auto-fitting all points
  // only when no live viewport was supplied.
  let viewport = null;
  if (
    viewportOverride?.center != null
    && Number.isFinite(viewportOverride.center.lat)
    && Number.isFinite(viewportOverride.center.lng)
    && Number.isFinite(viewportOverride.zoom)
  ) {
    viewport = {
      center: {
        lat: viewportOverride.center.lat,
        lng: viewportOverride.center.lng,
      },
      // Static Maps + our mercator projection both expect integer zoom.
      zoom: Math.max(2, Math.min(20, Math.round(viewportOverride.zoom))),
    };
  } else {
    const allPoints = [
      ...hubs.map(h => ({ lat: h.lat, lng: h.lng })),
      ...timeline.events.filter(ev => ev.lat != null && ev.lng != null).map(ev => ({ lat: ev.lat, lng: ev.lng })),
    ];
    viewport = computeViewport(allPoints, width, height, 100);
  }

  let baseImage = null;
  try {
    onProgress?.(0.03, 'Fetching base map…');
    baseImage = await loadStaticMap(viewport.center, viewport.zoom, width, height, mapStyleId);
  } catch (e) {
    // If Static Maps fails (billing / referrer restrictions) we still
    // produce a video, just on a plain slate background.
    baseImage = null;
  }
  const bgColor = backgroundColorForPreset(mapStyleId);

  const drawFrame = (playhead) => {
    renderFrame({
      ctx,
      width,
      height,
      baseImage,
      bgColor,
      center: viewport.center,
      zoom: viewport.zoom,
      hubs: hubs.filter(h => h.lat != null && h.lng != null),
      events: timeline.events,
      playhead,
      startMs: timeline.startMs,
      endMs: timeline.endMs,
      colormapId,
      lineEffect: resolvedLineEffect,
      lineColorMode,
      customLineColor,
      popOnHit,
      durationMs,
    });
  };

  const canWebCodecs =
    typeof window.VideoEncoder !== 'undefined' &&
    typeof window.VideoFrame !== 'undefined' &&
    typeof canvas.captureStream === 'function';

  onProgress?.(0.05, canWebCodecs ? 'Encoding H.264 (WebCodecs)…' : 'Recording via MediaRecorder…');
  let blob;
  let extension = 'mp4';
  if (canWebCodecs) {
    try {
      blob = await encodeWithWebCodecs({
        canvas,
        totalFrames,
        fps,
        drawFrame,
        onProgress: (p) => onProgress?.(0.05 + p * 0.9, 'Encoding H.264…'),
      });
    } catch (e) {
      // WebCodecs may be present but H.264 not supported (e.g. Firefox
      // without hardware encoder). Fall back to MediaRecorder path.
      const fallback = await encodeWithMediaRecorder({
        canvas,
        totalFrames,
        fps,
        drawFrame,
        onProgress: (p) => onProgress?.(0.05 + p * 0.9, 'Recording via MediaRecorder…'),
      });
      blob = fallback;
      extension = fallback.type.startsWith('video/mp4') ? 'mp4' : 'webm';
    }
  } else {
    blob = await encodeWithMediaRecorder({
      canvas,
      totalFrames,
      fps,
      drawFrame,
      onProgress: (p) => onProgress?.(0.05 + p * 0.9, 'Recording via MediaRecorder…'),
    });
    extension = blob.type.startsWith('video/mp4') ? 'mp4' : 'webm';
  }

  onProgress?.(0.98, 'Downloading…');
  triggerDownload(blob, `${filenameStem}.${extension}`);
  onProgress?.(1, 'Done.');
}
